import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import { getMyUploadsApi } from './myUploadsApi'
import {
  AppCsvUploadType,
  CoreCsvUploadType,
  type CsvUploadType,
  type UploadMetadata,
  type UploadScope,
} from '@repo/csv'
import type { SwitchableModule } from '@repo/permissions'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const myUploadsSearchSchema = z.object({
  ...datatableSearchSchema.shape,
  type: z.array(z.string()).optional(),
  uploadedBy: z.array(z.string()).optional(),
})

// CRT only: narrows the list to the uploads made into one import, from that import's "View history"
// link (`CrtImportHistoryButtons`). The other modules' pages parse with the base schema, which drops it.
export const crtMyUploadsSearchSchema = myUploadsSearchSchema.extend({
  attributes: z.record(z.string(), z.string()).optional(),
})

export type MyUploadsSearch = z.infer<typeof crtMyUploadsSearchSchema>

// Each module keeps its own upload log behind its own router; the page's module picks the router.
async function getMyUploads(queries: MyUploadsSearch, module: SwitchableModule) {
  const res = await getMyUploadsApi(module).$get({
    query: stringifyValues(queries),
  })

  const { myUploads, count } = await res.json()
  return { myUploads, count }
}

export type MyUploads = Awaited<ReturnType<typeof getMyUploads>>

async function getMyUploadsUploaders(types: readonly string[], module: SwitchableModule) {
  const res = await getMyUploadsApi(module).uploaders.$get({
    query: stringifyValues({ type: [...types] }),
  })

  const { uploaders } = await res.json()
  return uploaders
}

export type MyUploadsUploader = Awaited<ReturnType<typeof getMyUploadsUploaders>>[number]

async function getS3UrlForUpload<T extends CsvUploadType>(
  uploadType: T,
  localFileName: string,
  scope: UploadScope,
  attributes?: UploadMetadata<T>,
) {
  const api = getApi()
  const res = await api['upload-s3-data']['signed-url'].upload.$post({
    json: { uploadType, localFileName, scope, attributes: attributes as Record<string, string> },
  })

  const { url, fileKey, fileName } = await res.json()
  return { url, fileKey, fileName }
}

async function getS3UrlForDownload(
  uploadType: CoreCsvUploadType | AppCsvUploadType,
  fileName: string,
  scope: UploadScope,
) {
  const api = getApi()
  const res = await api['upload-s3-data']['signed-url'].download.$post({
    json: { uploadType, fileName, scope },
  })

  const { url } = await res.json()
  return { url }
}

export const useMyUploads = (queries: MyUploadsSearch, module: SwitchableModule) => {
  return useQuery({
    queryKey: ['my-uploads', module, queries],
    queryFn: async () => getMyUploads(queries, module),
  })
}

// The uploader options are the users who actually have uploads of these types, not the full user
// directory: listing users requires `users_view`, which this page's audience does not have.
export const useMyUploadsUploaders = (
  types: readonly string[],
  module: SwitchableModule,
  enabled = true,
) => {
  return useQuery({
    queryKey: ['my-uploads', 'uploaders', module, types],
    queryFn: async () => getMyUploadsUploaders(types, module),
    enabled,
  })
}

export const useUploadToS3 = <T extends CsvUploadType>(
  uploadType: T,
  scope: UploadScope = 'core',
  attributes?: UploadMetadata<T>,
  onSuccess?: () => void,
) => {
  const [isUploading, setIsUploading] = useState(false)
  const [isUploaded, setIsUploaded] = useState(false)
  const [progress, setProgress] = useState(0)
  const [isError, setIsError] = useState(false)
  const [fileMetadata, setFileMetadata] = useState<null | {
    fileKey: string
    fileName: string
    localFileName: string // the original file name from the user's computer, no functional purpose except for display
  }>(null)

  const uploadedFileMetadata = useMemo(() => {
    if (!isUploaded) return null

    return fileMetadata
  }, [fileMetadata, isUploaded])

  const onProgress = useCallback((event: ProgressEvent<EventTarget>) => {
    if (event.lengthComputable) {
      const percent = Math.round((event.loaded / event.total) * 100)
      setProgress(percent)
    }
  }, [])

  const onError = useCallback(() => {
    setIsUploading(false)
    setIsError(true)
  }, [])

  const onLoad = useCallback(
    (xhr: XMLHttpRequest) => {
      if (xhr.status === 200) {
        setProgress(100)
        setIsUploaded(true)
        onSuccess?.()
      } else {
        console.error('Upload failed with status:', xhr.status, xhr.responseText)

        setIsError(true)
      }
    },
    [onSuccess],
  )

  const handleUpload = async (file: File) => {
    setIsUploading(true)
    setProgress(0)

    try {
      // 1. Get the presigned URL from the backend using Hono RPC
      const { url, fileKey, fileName } = await getS3UrlForUpload(
        uploadType,
        file.name,
        scope,
        attributes,
      )

      console.log('Uploading to S3:', fileKey)
      setFileMetadata({ fileKey, fileName, localFileName: file.name })

      // 2. Upload with fetch and track progress
      const xhr = new XMLHttpRequest()
      xhr.open('PUT', url, true)
      xhr.setRequestHeader('Content-Type', file.type)

      xhr.upload.onprogress = onProgress
      xhr.onload = () => onLoad(xhr)
      xhr.onerror = onError

      xhr.send(file)
    } catch (isError) {
      console.error('Upload failed:', isError)
      setIsUploading(false)
    }
  }

  const resetS3Upload = () => {
    setProgress(0)
    setIsUploaded(false)
    setIsUploading(false)
    setIsError(false)
    setFileMetadata(null)
  }

  return {
    handleUpload,
    progress,
    isUploading,
    isUploaded,
    resetS3Upload,
    isError,
    uploadedFileMetadata,
  }
}

export function useDownloadFromS3(
  uploadType: CoreCsvUploadType | AppCsvUploadType,
  fileName: string,
  scope: UploadScope = 'core',
) {
  const { t } = useTranslation('uploads')
  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownload = async () => {
    setIsDownloading(true)

    try {
      const { url } = await getS3UrlForDownload(uploadType, fileName, scope)
      const res = await fetch(url, { credentials: 'omit' })
      if (res.status === 404) {
        toast.error(t('uploadReport.originalFileNotReady'))
        return
      }
      if (!res.ok) throw new Error(`Download failed with status ${res.status}`)
      const blob = await res.blob()
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      URL.revokeObjectURL(a.href)
      a.remove()
    } catch (err) {
      console.error('Download failed:', err)

      toast.error('Error', {
        description: 'Something went wrong',
      })
    } finally {
      setIsDownloading(false)
    }
  }

  return { handleDownload, isDownloading }
}
